import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { TransitionProps } from '@mui/material/transitions'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { Consultation } from '@/core/models/consultation.model'

import TabPanel from '@/components/ui/tab/TabPanel'
import AudioViewer from '@/components/ui/preview/AudioViewer'

import VitalData from '@/pages/consultation/containers/VitalData'
import Documents from '@/pages/diagnostic/containers/Documents'
import ConsultationFormsContainer from '@/pages/consultation/containers/ConsultationFormsContainer'
import PatientProfile from '@/pages/consultation/components/PatientProfile'
import Messenger from '@/pages/consultation/containers/Messenger'

export type ExpertiseConsultationDialogProps = {
  consultation: Consultation
  isAdded?: boolean
  onAdd?: (consultationId: string) => void
  onClose?: () => void
  onDelete?: (consultationId: string) => void
  open: boolean
  readOnly?: boolean
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export function ExpertiseConsultationDialog(props: ExpertiseConsultationDialogProps) {
  const { consultation, isAdded = false, onAdd, onClose, onDelete, open, readOnly = false } = props
  const { t } = useTranslation()

  const dictaphones = useMemo(() => {
    return (
      consultation?.dictaphones.map((dictaphone) => ({
        filename: dictaphone.filename,
        key: dictaphone.key,
        src: dictaphone.url,
        text: dictaphone.text ?? '',
      })) ?? []
    )
  }, [consultation])

  const tabs = useMemo(
    () => [
      { name: 'vitalData', label: t('page.consultation.tab.vitalData') },
      { name: 'documents', label: t('page.consultation.tab.documents') },
      { name: 'forms', label: t('page.consultation.tab.forms') },
      { name: 'transcriptions', label: t('page.consultation.tab.transcriptions') },
      { name: 'conversation', label: t('page.consultation.tab.conversation') },
    ],
    [t]
  )

  const [currentTab, setCurrentTab] = useState<string>(tabs[0].name)

  const handleSelectTab = (tab: { name: string; label: string }) => () => {
    setCurrentTab(tab.name)
  }

  const handleAdd = () => {
    onAdd?.(consultation._id)
  }

  const handleDelete = () => {
    onDelete?.(consultation._id)
  }

  return (
    <Dialog fullScreen onClose={onClose} open={open} scroll="paper" TransitionComponent={Transition}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Chip
            color="secondary"
            label={
              <Stack direction="row" alignItems="center">
                <span>
                  {dayjs(consultation.startDate).format('DD/MM/YYYY')} - <b>{dayjs(consultation.startDate).format('HH[H]mm')}</b>
                </span>
              </Stack>
            }
            variant="filled"
          />
          <Tooltip title={t('common:button.close.tooltip')}>
            <IconButton onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="h2" component="div" noWrap sx={{ fontSize: '24px', fontWeight: 700, mt: '16px', mb: '10px' }}>
          {t('page.consultation.title', { patient: _.trim(`${consultation.patient?.userInfo.surname || ''} ${consultation.patient?.userInfo.name || ''}`) })}
        </Typography>
        <Stack direction="row" spacing={3}>
          {tabs.map((tab) => (
            <Button
              key={tab.name}
              onClick={handleSelectTab(tab)}
              sx={[
                {
                  color: colors.gray500,
                  fontSize: '14px',
                  fontWeight: 500,
                },
                tab.name === currentTab && {
                  backgroundColor: colors.indigo100,
                  color: colors.indigo700,
                  '&:hover': {
                    backgroundColor: colors.indigo100,
                  },
                },
              ]}
            >
              {tab.label}
            </Button>
          ))}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <TabPanel value="vitalData" index={currentTab}>
          <VitalData consultationId={consultation._id} />
        </TabPanel>
        <TabPanel value="documents" index={currentTab}>
          <Documents consultationId={consultation._id} readOnly={true} />
        </TabPanel>
        <TabPanel value="forms" index={currentTab}>
          <ConsultationFormsContainer consultationId={consultation._id} />
        </TabPanel>
        <TabPanel value="transcriptions" index={currentTab}>
          <Stack divider={<Divider />} spacing={3}>
            {dictaphones?.map((dictaphone) => (
              <Stack key={dictaphone.key} spacing={2}>
                <AudioViewer src={dictaphone.src} sx={{ width: '100%' }} />
                <TextField fullWidth InputProps={{ readOnly: true }} minRows={4} multiline={true} defaultValue={dictaphone.text} />
              </Stack>
            ))}
          </Stack>
        </TabPanel>
        <TabPanel value="conversation" index={currentTab}>
          <Messenger consultationStatus={consultation.status} chatRoomId={consultation.chatRoom} />
        </TabPanel>
      </DialogContent>
      {!readOnly && (
        <DialogActions sx={{ pt: 2 }}>
          {isAdded ? (
            <Button onClick={handleDelete} variant="contained" color="error">
              {t('common:button.delete.label')}
            </Button>
          ) : (
            <Button onClick={handleAdd} variant="contained" color="primary">
              {t('common:button.add.label')}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default ExpertiseConsultationDialog
